<script lang="ts">
  import { link, location } from 'svelte-spa-router'
  import { sidebarCollapsed } from '../stores/ui'
  import { tStore } from '../i18n'

  interface NavItem {
    path: string
    labelKey: string
    icon: string
  }

  const navItems: NavItem[] = [
    { path: '/', labelKey: 'nav.dashboard', icon: '◆' },
    { path: '/certificate-analysis', labelKey: 'nav.certificateAnalysis', icon: '▤' },
    { path: '/filters', labelKey: 'nav.filters', icon: '★' },
    { path: '/sources', labelKey: 'nav.sources', icon: '⌖' },
    { path: '/notifications', labelKey: 'nav.notifications', icon: '◉' },
    { path: '/archive', labelKey: 'nav.archive', icon: '🗄' },
  ]

  const settingsItems: NavItem[] = [
    { path: '/collection', labelKey: 'nav.collection', icon: '▶' },
    { path: '/ingestion-runs', labelKey: 'nav.ingestionRuns', icon: '↻' },
    { path: '/ai-history', labelKey: 'nav.aiHistory', icon: '🕘' },
    { path: '/settings/company-profile', labelKey: 'nav.companyProfile', icon: '◧' },
  ]

  // The "Settings" group is collapsed by default; the chevron reveals the
  // collection-trigger and ingestion-runs entries on demand.
  let settingsExpanded = $state(false)

  const rawVersion = import.meta.env.VITE_APP_VERSION || 'dev'
  const appVersion = rawVersion.startsWith('v') ? rawVersion.slice(1) : rawVersion

  function isActive(itemPath: string, currentPath: string): boolean {
    if (itemPath === '/') return currentPath === '/'
    return currentPath === itemPath || currentPath.startsWith(`${itemPath}/`)
  }
</script>

<aside class="sidebar" class:sidebar--collapsed={$sidebarCollapsed} aria-label="Main navigation">
  <div class="sidebar__brand">
    <a href="/" use:link class="sidebar__mark" aria-label={$tStore('nav.dashboard')}>A</a>
  </div>

  <nav>
    <ul>
      {#each navItems as item (item.path)}
        {@const active = isActive(item.path, $location)}
        <li>
          <a
            href={item.path}
            use:link
            class:active
            aria-current={active ? 'page' : undefined}
            title={$tStore(item.labelKey)}
          >
            <span class="icon" aria-hidden="true">{item.icon}</span>
            <span class="label">{$tStore(item.labelKey)}</span>
          </a>
        </li>
      {/each}
    </ul>

    <button
      type="button"
      class="nav-section-toggle"
      aria-expanded={settingsExpanded}
      onclick={() => (settingsExpanded = !settingsExpanded)}
      title={$tStore('nav.settings')}
    >
      <span class="nav-section-title label">{$tStore('nav.settings')}</span>
      <span
        class="nav-section-chevron label"
        class:nav-section-chevron--open={settingsExpanded}
        aria-hidden="true">▸</span
      >
    </button>
    {#if settingsExpanded}
      <ul>
        {#each settingsItems as item (item.path)}
          {@const active = isActive(item.path, $location)}
          <li>
            <a
              href={item.path}
              use:link
              class:active
              aria-current={active ? 'page' : undefined}
              title={$tStore(item.labelKey)}
            >
              <span class="icon" aria-hidden="true">{item.icon}</span>
              <span class="label">{$tStore(item.labelKey)}</span>
            </a>
          </li>
        {/each}
      </ul>
    {/if}
  </nav>

  <div class="sidebar-version" aria-label="Application version">
    <span class="sidebar-version__text label">{appVersion}</span>
  </div>
</aside>

<style lang="scss">
  @use '../assets/styles/variables' as v;

  .sidebar {
    width: v.$sidebar-width;
    flex-shrink: 0;
    background: v.$color-sidebar-bg;
    color: v.$color-sidebar-fg;
    transition: width 0.2s ease;
    overflow: hidden;
    position: sticky;
    top: 0;
    align-self: flex-start;
    height: 100vh;
    display: flex;
    flex-direction: column;
    border-right: 1px solid v.$color-sidebar-divider;

    &--collapsed {
      width: v.$sidebar-width-collapsed;

      .label {
        opacity: 0;
        pointer-events: none;
      }
    }
  }

  .sidebar__brand {
    display: flex;
    align-items: center;
    padding: 0 v.$space-md;
    height: v.$header-height;
    border-bottom: 1px solid v.$color-sidebar-divider;
    flex-shrink: 0;
  }

  .sidebar__mark {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border-radius: v.$radius-md;
    background: linear-gradient(135deg, v.$color-primary, #6366f1);
    color: #fff;
    font-weight: 700;
    font-size: 0.9375rem;
    box-shadow: v.$shadow-sm;
    flex-shrink: 0;
    text-decoration: none;
    cursor: pointer;

    &:hover {
      opacity: 0.85;
    }
  }

  nav {
    flex: 1;
    overflow-y: auto;
  }

  .sidebar-version {
    margin-top: auto;
    padding: v.$space-sm v.$space-md;
    border-top: 1px solid v.$color-sidebar-divider;
    text-align: center;
    flex-shrink: 0;

    &__text {
      font-size: 0.7rem;
      color: v.$color-text-subtle;
      letter-spacing: 0.03em;
      user-select: none;
    }
  }

  nav ul {
    list-style: none;
    margin: 0;
    padding: v.$space-md v.$space-sm;
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  .nav-section-toggle {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: v.$space-sm;
    width: calc(100% - #{v.$space-sm} * 2);
    margin: 0 v.$space-sm;
    padding: v.$space-sm v.$space-sm 0;
    background: none;
    border: none;
    border-top: 1px solid v.$color-sidebar-divider;
    color: inherit;
    cursor: pointer;
    font-family: inherit;

    &:hover .nav-section-title,
    &:hover .nav-section-chevron {
      color: v.$color-sidebar-fg-strong;
    }
  }

  .nav-section-title {
    font-size: 0.6875rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: v.$color-text-subtle;
  }

  .nav-section-chevron {
    font-size: 0.75rem;
    color: v.$color-text-subtle;
    transition:
      transform 0.15s ease,
      opacity 0.15s ease;

    &--open {
      transform: rotate(90deg);
    }
  }

  a {
    display: flex;
    align-items: center;
    gap: 0.625rem;
    padding: 0.5rem 0.75rem;
    color: inherit;
    text-decoration: none;
    white-space: nowrap;
    font-size: 0.875rem;
    font-weight: 500;
    border-radius: v.$radius-sm;
    position: relative;
    transition:
      background-color 120ms ease,
      color 120ms ease;

    &:hover {
      background: v.$color-sidebar-hover;
      color: v.$color-sidebar-fg-strong;
      text-decoration: none;
    }

    &.active {
      background: v.$color-sidebar-active-bg;
      color: v.$color-sidebar-active-fg;

      &::before {
        content: '';
        position: absolute;
        left: -8px;
        top: 8px;
        bottom: 8px;
        width: 3px;
        border-radius: 0 v.$radius-sm v.$radius-sm 0;
        background: v.$color-primary;
      }
    }
  }

  .icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    font-size: 0.875rem;
    color: v.$color-text-subtle;
    flex-shrink: 0;
  }

  a.active .icon {
    color: v.$color-sidebar-active-fg;
  }

  .label {
    transition: opacity 0.15s ease;
  }

  @media (max-width: v.$bp-md) {
    .sidebar {
      width: v.$sidebar-width-collapsed;

      .label {
        opacity: 0;
        pointer-events: none;
      }
    }
  }
</style>
